// hooks/useCreateNewReservation.ts
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { RootState } from "../store"; // Adjust the import based on your file structure
import { CreateVenueReservationData } from "../../api/types";
import Logger from "../helpers/logger";
import ErrorTracker from "../helpers/errorTracker";
import { useVenueCreateNewReservationMutation } from "../../api/reservations/reservationsApiSlice";
import { PersonalData } from "../../types/bookingCreation";
import KolulaStorageManager from "../../storage/KolulaStorageManager";
import { setVenueReservationSuccess } from "../slice/apartmentRental/apartmentRentalSlice";
import { notifyBookingFailure } from "../helpers/failureNotification";
import { requestWindow, resolveRequestDates } from "./reservationWindow";
import { requestAmount } from "./createReservationFlow";
import { useSelectedLocationTimeZone } from "../../util/locationTimeZone";
import { coverageChoiceFields, selectBookPrivately, useIsCoveredBooking } from "../../modules/bookLocation/common/hooks/useCoveredBooking";
import { selectRoomFamilyGroupId } from "./roomGroupId";
import { bookingRecipient } from "../../util/bookingRecipient";
import { requestCarriesEventType, requestCarriesTimeOfDay } from "../../util/roomRequestShape";

/**
 * Creates the reservation-request order (`POST /orders/from-reservation`).
 * Built for the venue flow; Wire 2 (service-configuration plan) parameterized
 * it so a use case configured to `directBooking: false` — first the apartment
 * flow — files the same kind of request. Venue behavior is unchanged: with no
 * argument (or `"venue"`) every branch below takes the venue path.
 */

const recipientRequestField = (
  recipient: PersonalData | undefined
): Pick<CreateVenueReservationData, "recipientData"> =>
  recipient
    ? {
        recipientData: {
          firstname: recipient.firstName,
          lastname: recipient.lastName,
          email: recipient.email,
        },
      }
    : {};

export const useCreateNewReservation = (useCase?: string) => {
  const indexDBStore = new KolulaStorageManager();
  // The selected room's own group first, `assetGroupId` as fallback — the
  // one rule shared with `createReservationFlow` (see `selectRoomFamilyGroupId`).
  // `assetGroupId` is only written when the details fetch lands, so it can be
  // empty (refresh on the summary) or still name a previously viewed room.
  const roomFamilyGroupId = useSelector(selectRoomFamilyGroupId);
  const orderData = useSelector(
    (state: RootState) => state.bookingCreation.orderData
  );
  const personalData: PersonalData = useSelector(
    (state: RootState) => state.bookingCreation.personalData
  );
  const startTime = useSelector(
    (state: RootState) => state.bookingCreation.orderData.startTime
  );
  const endTime = useSelector(
    (state: RootState) => state.bookingCreation.orderData.endTime
  );
  const recipientSource = useSelector(
    (state: RootState) => ({
      isForSomeoneElse: state.bookingCreation.isForSomeoneElse,
      someoneElseData: state.bookingCreation.someoneElseData,
      recipientData: state.bookingCreation.recipientData,
    }),
    shallowEqual
  );
  const additionalServices = useSelector(
    (state: RootState) => state.bookingCreation.additionalServices
  );

  // What this screen told the customer: covered, nothing to pay. Also
  // #1810: a cost centre is taken on a covered booking only; the server
  // refuses one on any other, so a value typed while the room was covered is
  // not carried onto a booking that no longer is.
  const expectsCoverage = useIsCoveredBooking();
  const bookPrivately = useSelector(selectBookPrivately);
  // The booked location's clock (#3219) — the server reads the window there.
  const timeZone = useSelectedLocationTimeZone();

  const [createNewReservationMutation] = useVenueCreateNewReservationMutation();
  const dispatch = useDispatch();

  const createNewReservation = async () => {
    try {
      const groupId = roomFamilyGroupId;
      if (!groupId || !orderData) {
        // Never a silent return: "the button did nothing" is the one
        // response that guarantees a support call (see catch below).
        const error = new Error(
          `createNewReservation submitted without ${
            !groupId ? "a group id" : "order data"
          }`
        );
        Logger.for("createNewReservation").error(error);
        ErrorTracker.trackException(error);
        notifyBookingFailure(dispatch, error);
        return;
      }

      const { fromDate, toDate } = resolveRequestDates(orderData, timeZone);

      // No argument means the venue flow, which this request was built for.
      const requestService = useCase ?? "venue";
      const window = requestWindow(requestCarriesTimeOfDay(requestService), fromDate, toDate, startTime, endTime, timeZone);
      const newReservation: CreateVenueReservationData = {
        reservation: {
          // Never below 1: a station flow abandoned at its group step leaves
          // `orderData.amount` at 0, and the server's capacity check counts
          // the booking by it (`assertCapacityAvailableTx`, `amount ?? 1`).
          amount: requestAmount(orderData.amount),
          groupId,
          fromDate: window.fromDate,
          toDate: window.toDate,
          guests: orderData.numberOfGuests,
          // No price is sent. The server computes the stay price from the
          // group's own rates at creation (ADR-0020) — this flow used to submit
          // a number it worked out here, with a weekend rule that disagreed
          // with the very summary the customer was shown.
        },
        service: requestService,
        ...(requestCarriesEventType(requestService) ? { eventType: orderData.eventType! } : {}),
        // The venue flow creates the booking in one call — unlike room rental,
        // which creates first and then PATCHes the selection onto the
        // reservation. Without this the guest's ticked services were never
        // sent and the booking came out empty in the admin UI.
        additionalServices: additionalServices.map((service) => ({
          serviceId: service.serviceId,
          quantity: service.quantity,
        })),
        contactData: {
          firstname: personalData.firstName,
          lastname: personalData.lastName,
          email: personalData.email,
          telephone: personalData.phone,
          company: personalData.company,
          address: personalData.address,
          zipCode: personalData.zipCode,
          city: personalData.city,
          country: personalData.country,
        },
        comment: orderData.comment || null,
        // The person the booking is made for, when it is not the booker. The
        // covered and request bookings come through here, not through the
        // payment routes, so without it they lost that person. Only "booking
        // for someone else": room and venue have no recipient step, so a
        // `recipientData` left over from a p2p or key-handover booking in the
        // same session must not ride along.
        ...recipientRequestField(
          recipientSource.isForSomeoneElse
            ? bookingRecipient(recipientSource)
            : undefined
        ),
        // #1926: which organization covers this booking, when the member holds
        // several and the room does not answer it. A PREFERENCE — the server
        // honours it only if the session actually holds that membership, and
        // ignores it entirely for anyone who is not covered. And "Privat
        // buchen – ich zahle selbst": the server declines coverage for this
        // one booking (backend app/order/bookingCoverage.ts). The price
        // preview sends the same fields (`coverageChoiceFields`).
        ...coverageChoiceFields(orderData.coveringOrganizationId, bookPrivately),
        // #1810: only what the member typed; untouched, the server applies
        // the default the field showed. Taken on a covered booking only.
        ...(expectsCoverage && orderData.costCenter !== undefined
          ? { costCenter: orderData.costCenter.trim() || null }
          : {}),
        // The screen promised "covered, nothing to pay". If the server cannot
        // confirm that (contract ended or membership revoked since the search,
        // no attributable organization), it refuses instead of filing an
        // unpaid request — which would skip the checkout of a direct-booking
        // tenant and still read "Buchung bestätigt!" here. Never grants
        // coverage: the server only uses it to refuse.
        ...(expectsCoverage ? { expectsCoverage: true } : {}),
      };

      // Call the mutation function
      const response = await createNewReservationMutation(
        newReservation
      ).unwrap();

      if (response.id) {
        await indexDBStore.insertInternalMultipleOrdersIds([response.id]);
        await indexDBStore.deletePersonalData();
        dispatch(setVenueReservationSuccess(5));
      }
    } catch (error) {
      Logger.for("createNewReservation").error(error);
      ErrorTracker.trackException(error as Error);

      // Every failure here used to end in the log and nowhere else: the button
      // simply did nothing and the customer had no idea why. The member path
      // makes that untenable — a membership revoked between choosing the room
      // and submitting answers 403 (#1926), and "nothing happened" is the one
      // response that guarantees a support call.
      //
      // The mapping was inlined here first (#1964) and became shared in #1970;
      // the two PRs merged in the other order, so this was briefly the second
      // place deciding what a failed booking says. It also answers 409 now, and
      // reaches the Norwegian strings the inline keys never had.
      notifyBookingFailure(dispatch, error);
    }
  };

  return createNewReservation;
};
